import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { cashHandover, cashReturn, getCashBook } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { KIND_LABEL } from "./pay/payModel";
import { fmtDay, rupees } from "./rates/rateModel";
import { newRequestId } from "./rounds/entryQueue";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./pay/karkhana-pay.scss";
import { useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/**
 * Cash in the supervisor's hand: handed over by the studio, paid out to workers in cash, returned.
 * At day's end the notes in their pocket should match "Cash in hand".
 */
const KarkhanaCashBookPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const supervisor = isKarkhanaSupervisor(role);
  // "Hand over" on the Pay page opens straight on that supervisor.
  // The supervisor whose book is open (?supervisor=…), replaced on each pick so Back leaves the page.
  const [who, setWho] = useAddressParam("supervisor");
  const [book, setBook] = useState<any>(null);
  const [form, setForm] = useState<null | "HANDOVER" | "RETURN">(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res: any = await getCashBook(supervisor ? undefined : who || undefined);
      if (res?.success) setBook(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load the cash book"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("The cash book needs a connection."), type: "warning" });
    }
  }, [supervisor, who]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const body = { amount: amount.trim(), note: note.trim() || undefined, clientRequestId: newRequestId() };
      const res: any = form === "HANDOVER" ? await cashHandover(who, body) : await cashReturn(supervisor ? undefined : who, body);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      setBook(res.data);
      setForm(null);
      setAmount("");
      setNote("");
      notifications({ title: t("Saved"), message: form === "HANDOVER" ? "Cash handed over" : "Cash returned", type: "success" });
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was recorded. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  if (!canUseRounds(role)) return null;
  const showBook = supervisor || !!who;
  const inHand = Number(book?.cashInHand || 0);

  return (
    <div className="gi-registry-page kw-page kr-rounds">
      <PageHeader title={t("Cash book")} back={kp.pay()} backLabel={t("Back")} />

      {!supervisor ? (
        <>
          <p className="kw-lede">{t("Hand cash to a supervisor to pay people. Every cash payment or advance they record comes off what they hold.")}</p>
          {(book?.supervisors || []).length === 0 && book ? (
            <div className="kw-empty">
              {t("No supervisors yet.")} <Link to={kp.supervisors()}>{t("Add one")}</Link>.
            </div>
          ) : null}
          {(book?.supervisors || []).map((s: any) => (
            <button key={s.id} type="button" className={`kr-loom${who === s.id ? " is-WORKED" : ""}`} onClick={() => setWho(s.id)}>
              <span className="kr-loom__code" style={{ fontSize: 14 }}>
                {s.name?.slice(0, 2)?.toUpperCase()}
              </span>
              <span className="kr-loom__main">
                <span className="kr-loom__line">{s.name}</span>
                <span className="kr-loom__line kw-muted">{s.phone || s.email || ""}</span>
              </span>
              <span className="kr-loom__state">{rupees(s.cashInHand, 0)} in hand</span>
            </button>
          ))}
        </>
      ) : null}

      {showBook && book?.cashInHand !== undefined ? (
        <>
          <div className="kpay-balances">
            <div className={`kpay-balance${inHand < 0 ? "" : " is-due"}`}>
              <span>{t("Cash in hand")}</span>
              <strong style={{ color: inHand < 0 ? "#b3261e" : undefined }}>{rupees(book.cashInHand)}</strong>
              {inHand < 0 ? <small>{t("Paid out more than was handed over")}</small> : null}
            </div>
            <div className="kpay-balance">
              <span>{t("Handed over · paid out · returned")}</span>
              <small>
                {rupees(book.handedOver, 0)} · {rupees(book.paidOut, 0)} · {rupees(book.returned, 0)}
              </small>
            </div>
          </div>
          <div className="kw-toolbar">
            {!supervisor ? (
              <Button className="ResetBtn" onClick={() => setForm("HANDOVER")}>
                {t("Hand over cash")}
              </Button>
            ) : null}
            <Button outline disabled={inHand <= 0} onClick={() => setForm("RETURN")}>
              {supervisor ? t("Give cash back") : t("Record cash returned")}
            </Button>
          </div>

          {form ? (
            <div className="kw-card">
              <span className="kr-label">{form === "HANDOVER" ? t("Cash handed over") : t("Cash given back")}</span>
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("Amount, e.g. 5000")} aria-label="Amount" autoFocus />
              <span className="kr-label">{t("Note (optional)")}</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
              <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
                <Button outline onClick={() => setForm(null)}>
                  {t("Cancel")}
                </Button>
                <Button className="ResetBtn" disabled={busy || !amount.trim()} onClick={save}>
                  {busy ? t("Saving…") : t("Save {{amount}}", { amount: rupees(amount || 0) })}
                </Button>
              </div>
            </div>
          ) : null}

          <h3 className="kr-group-title">{t("Movements")}</h3>
          {!(book.movements || []).length ? <div className="kw-empty">{t("No cash moved yet.")}</div> : null}
          {(book.movements || []).map((m: any, i: number) => (
            <div key={m.id || i} className="kr-pay__row kpay-entry">
              <span>
                <strong>{m.kind === "HANDOVER" ? t("Handed over") : m.kind === "RETURN" ? t("Given back") : t(KIND_LABEL[m.kind] || m.kind)}</strong> · {fmtDay(m.date)}
                {m.person ? ` · ${m.person}` : ""}
                {m.note ? ` · “${m.note}”` : ""}
              </span>
              <span style={{ color: m.in ? "#1e7b34" : undefined }}>
                {m.in ? "+" : "−"}
                {rupees(m.amount)}
              </span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
};

export default KarkhanaCashBookPage;
